/**
 * CallFeedbackCard (LUM-177)
 *
 * Post-call feedback prompt for pilot testers. InterviewPage renders it
 * after a call ends, and only when the respondent's profile has
 * feedback_capture_enabled (SQL-toggled — real respondents never see
 * it). Non-blocking by design: dismissible without submitting, never
 * gates starting another call.
 *
 * `inline` (default since live call: a tester's clean 10-min interview
 * produced no visible popup — floating chrome is viewport-fragile)
 * renders as a hairline wrap-up section in the page flow; `floating`
 * keeps the original fixed bottom-right card.
 */

import {
  ActionIcon,
  Button,
  CloseButton,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconStar, IconStarFilled } from '@tabler/icons-react';
import { useState } from 'react';
import { apiFetch } from '@/lib/api-client';
import { logger } from '@/utils/logger';

export interface CallFeedbackCardProps {
  /** OpenAI Realtime call id (rtc_...) of the call that just ended. */
  callId: string;
  onDismiss: () => void;
  variant?: 'inline' | 'floating';
}

const RATINGS = [1, 2, 3, 4, 5] as const;

export function CallFeedbackCard({
  callId,
  onDismiss,
  variant = 'inline',
}: Readonly<CallFeedbackCardProps>) {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Server contract: at least one of rating / non-empty comment.
  const canSubmit = rating !== null || comment.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch('/api/realtime/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callId,
          ...(rating !== null ? { rating } : {}),
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        }),
      });
      if (!res.ok) {
        throw new Error(`feedback POST failed: ${res.status}`);
      }
      setSubmitted(true);
    } catch (err) {
      logger.error('Failed to submit call feedback', { err });
      setError('Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      shadow={variant === 'floating' ? 'md' : undefined}
      radius={variant === 'floating' ? 'md' : 0}
      p={variant === 'floating' ? 'md' : 0}
      withBorder={variant === 'floating'}
      aria-label="Call feedback"
      style={
        variant === 'floating'
          ? {
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 320,
              maxWidth: 'calc(100vw - 32px)',
              zIndex: 300,
            }
          : {
              // Wrap-up hairline register (LUM-185): a section, not a card.
              background: 'transparent',
              borderBottom: '1px solid rgba(29, 36, 50, 0.16)',
              padding: '4px 2px 20px',
              maxWidth: 480,
            }
      }
    >
      <Stack gap="sm">
        <Group
          justify="space-between"
          align="flex-start"
          gap="xs"
          wrap="nowrap"
        >
          <Text fw={600} fz="sm" c="navy.8">
            How was that conversation?
          </Text>
          <CloseButton
            size="sm"
            onClick={onDismiss}
            aria-label="Dismiss feedback"
          />
        </Group>
        {submitted ? (
          <Text fz="sm" c="dimmed">
            Thanks — your feedback has been recorded.
          </Text>
        ) : (
          <>
            <Group gap={4}>
              {RATINGS.map(value => (
                <ActionIcon
                  key={value}
                  variant="subtle"
                  color="yellow.7"
                  aria-label={`Rate ${value} of 5`}
                  onClick={() => setRating(value)}
                  disabled={submitting}
                >
                  {rating !== null && value <= rating ? (
                    <IconStarFilled size={20} />
                  ) : (
                    <IconStar size={20} />
                  )}
                </ActionIcon>
              ))}
            </Group>
            <Textarea
              placeholder="Anything to add? (optional)"
              value={comment}
              onChange={e => setComment(e.currentTarget.value)}
              maxLength={2000}
              autosize
              minRows={2}
              maxRows={4}
              disabled={submitting}
            />
            {error && (
              <Text fz="xs" c="red.6">
                {error}
              </Text>
            )}
            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                onClick={onDismiss}
                disabled={submitting}
              >
                Not now
              </Button>
              <Button
                size="xs"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!canSubmit}
              >
                Send feedback
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Paper>
  );
}
